import { Component, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { takeUntil, first, map, debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';
import { TenantService, StreamService } from '../services';
import * as _ from 'lodash';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { Router } from '@angular/router';

@Component({
    styles: [`
        mat-list-item.mat-list-option.mat-list-item-with-avatar {
            height: 80px;
        }
        .create-topic-link {
            cursor: pointer;
            color: blue;
            text-decoration-color: blue;
            text-decoration-line: underline;
        }
    `],
    template: `
        <div mat-dialog-title>Select Topics</div>
        <div mat-dialog-content style="height: calc(100vh - 100px); width: 650px;">
            <div>
                <mat-form-field><input matInput [formControl]="searchTerm"></mat-form-field>
            </div>
            <mat-tab-group style="max-width: 600px;" [(selectedIndex)]="selectedTabIndex">
                <mat-tab [label]="tenantCatalogTitle">
                    <div *ngIf="teamTopicResults$ | async as results; else spinner">
                        <div *ngIf="!results.length">
                            <h5 style="text-align: center">
                                <p>Your Workplace does not have any topics yet.</p> 
                                <p>Once you have created topics you will be able to select them here.</p> 
                                <p>You can create topics <a class="create-topic-link" (click)="goToCreateTopic()">here</a> <span *ngIf="browseableLabel"> or select the {{browseableLabel}} tab to view and select topics in that library</span>.</p>
                            </h5>
                        </div>
                        <topic-selection-list [topics]="results"
                                              [existingTopicIds]="existingTopics" 
                                              (selectionChange)="teamSelectionChanged($event)"></topic-selection-list>
                    </div>    
                </mat-tab>
                <mat-tab *ngFor="let catalog of availableCatalogs" [label]="catalog.label">
                    <topic-selection-list *ngIf="catalogTopicResultsMap[catalog.catalogId] | async as results; else spinner"
                                          [topics]="results" 
                                          [existingTopicIds]="existingTopics"
                                          (selectionChange)="catalogSelectionChanged(catalog.catalogId, $event)"></topic-selection-list>
                </mat-tab> 
                <mat-tab label="Search Results"> 
                    <h4>Results based on title and description</h4>
                    <div *ngIf="nameBasedSearchResults$| async as searchResults; else no_name_search">
                    <div *ngIf="searchResults && !searchResults.length">No matching Topics</div>
                        <topic-selection-list *ngIf="searchResults && searchResults.length" 
                                            [topics]="searchResults"
                                            [existingTopicIds]="existingTopics" 
                                            (selectionChange)="selectionChanged($event)"></topic-selection-list>
                    </div>
                    <h4>Results based on Article content</h4>
                    <div *ngIf="itemBasedSearchResults$| async as searchResults; else no_item_search">
                        <div *ngIf="searchResults && !searchResults.length">No matching Topics</div>
                        <topic-selection-list *ngIf="searchResults && searchResults.length" 
                                            [topics]="searchResults" 
                                            [existingTopicIds]="existingTopics"
                                            (selectionChange)="selectionChanged($event)"></topic-selection-list>
                    </div>
                    <ng-template #no_name_search>
                        <div style="margin: 25px;">
                            <span *ngIf="!noTouchingName">Enter a term to search for Topics</span>
                            <spinner *ngIf="noTouchingName"></spinner>
                        </div>
                    </ng-template>
                    <ng-template #no_item_search>
                        <div style="margin: 25px;">
                            <span *ngIf="!noTouchingArticle">Enter a term to search for Topics</span>
                            <spinner *ngIf="noTouchingArticle"></spinner>
                        </div>
                    </ng-template>
                </mat-tab>
            </mat-tab-group>
        </div>
        <div mat-dialog-actions>
            <button mat-raised-button 
                    color="primary" 
                    [disabled]="!selectedTopics().length" 
                    [mat-dialog-close]="selectedTopics()">Add</button>
            <button mat-button mat-dialog-close>Cancel</button>
        </div>
        <ng-template #spinner><spinner></spinner></ng-template>
`})
export class TopicSelectorDialogComponent implements OnDestroy {
    noTouchingName: boolean;
    noTouchingArticle: boolean;
    searchTerm: FormControl;
    tenantCatalogTitle: string;
    cleanupTime = new Subject<void>();
    teamTopicResults$ = new BehaviorSubject<any[] | null>(null);
    catalogTopicResultsMap = {};
    itemBasedSearchResults$ = new  BehaviorSubject<any[] | null>(null);
    nameBasedSearchResults$ = new  BehaviorSubject<any[] | null>(null);
    availableCatalogs: { catalogId: string, label: string, browsingEnabled: boolean }[] = [];
    searchSelectedTopics = [];
    teamSelectedTopics = [];
    catalogSelectedTopics = {};
    selectedTabIndex: number;
    existingTopics: string[];
    browseableLabel: any;

    constructor(@Inject(MAT_DIALOG_DATA) data,
                private dialogRef: MatDialogRef<TopicSelectorDialogComponent>,
                private router: Router, 
                fb: FormBuilder, 
                streamService: StreamService, 
                tenantService: TenantService) {
        this.existingTopics = data.currentTopics;
        this.searchTerm = fb.control([]);
        
        tenantService.getUnauthTenantInfo()
        .pipe(
            first(),
            tap(tenant => this.tenantCatalogTitle = tenant.settings.title || 'Team Topics'), 
            map(tenant => tenant.availableCatalogs.filter(catalog => catalog.browsingEnabled)),
            tap(availableCatalogs => {
                if (availableCatalogs.length) {
                    this.browseableLabel = availableCatalogs[0].label;
                }
                availableCatalogs.forEach(catalog => 
                    streamService.getCatalogStreams(catalog.catalogId, { rows: 500 }).pipe(first())
                    .subscribe(results => this.catalogTopicResultsMap[catalog.catalogId] = new  BehaviorSubject<any[] | null>(results.content))
                );
            })
        )
        .subscribe(availableCatalogs => this.availableCatalogs = availableCatalogs)
        
        streamService.getTenantStreams({ 
            includeCatalogs: false,
            published: true,
            rows: 1000
        }).pipe(first())
        .subscribe(topicResults => this.teamTopicResults$.next(topicResults.content));

        this.searchTerm.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this.cleanupTime)
        ).subscribe(term => {
            this.selectedTabIndex = this.availableCatalogs.length + 1; // available catlog count + 1 for teams = 0 based index of search tab
            this.itemBasedSearchResults$.next(null);
            this.nameBasedSearchResults$.next(null);
            if (!_.isEmpty(term)){
                this.noTouchingArticle = true;
                this.noTouchingName = true;
                this.searchSelectedTopics = [];
                streamService.getRecommendedStreams({  rows: 100, includeCatalogs: true, published: true, type: 'item_search', search: term, includeTopics: true }).pipe(first())
                .subscribe(streamRes => {
                    this.itemBasedSearchResults$.next(streamRes.content);
                    this.noTouchingArticle = false;
                });
                streamService.getTenantStreams({  rows: 100, includeCatalogs: true, published: true, type: 'COLLECTION', term: term }).pipe(first())
                .subscribe(streamRes => {
                    this.nameBasedSearchResults$.next(streamRes.content);
                    this.noTouchingName = false;
                });
            }
        })
    }

    teamSelectionChanged(evt: MatSelectionListChange) {
        this.teamSelectedTopics = evt.source.selectedOptions.selected.map(opt => opt.value);
    }

    catalogSelectionChanged(catalogId, evt: MatSelectionListChange) {
        this.catalogSelectedTopics[catalogId] = evt.source.selectedOptions.selected.map(opt => opt.value);
    }

    selectionChanged(evt: MatSelectionListChange) {
        const topic = evt.option.value;
        const currentIndex = this.searchSelectedTopics.indexOf(topic);
        if (evt.option.selected && currentIndex === -1) {
            this.searchSelectedTopics.push(topic);
        } else {
            this.searchSelectedTopics.splice(currentIndex, 1);
        }
    }

    selectedTopics() {
        return _.flatten(this.searchSelectedTopics.concat(this.teamSelectedTopics, _.map(this.catalogSelectedTopics, topics => topics))); 
    }

    goToCreateTopic(): void {
        this.router.navigate(['/topics', 'create']);
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}